import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronLeft, CheckCircle, Truck, Package } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../utils/api.js';
import { useAuth } from '../../context/AuthContext.jsx';
import StatusBadge from '../../components/ui/Badge.jsx';
import Button from '../../components/ui/Button.jsx';
import Card from '../../components/ui/Card.jsx';
import FileUpload from '../../components/FileUpload/FileUpload.jsx';
import { formatIST } from '../../utils/formatters.js';

export default function DeliveryNoteDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const qc = useQueryClient();
  const [receiverRemarks, setReceiverRemarks] = useState('');

  const { data: dn, isLoading, refetch } = useQuery({
    queryKey: ['delivery-notes', id],
    queryFn: () => api.get(`/delivery-notes/${id}`).then(r => r.data.data),
  });

  const transition = useMutation({
    mutationFn: (data) => api.post(`/delivery-notes/${id}/transition`, data).then(r => r.data.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['delivery-notes'] }); refetch(); },
  });

  if (isLoading) return <div className="p-8 text-center text-gray-400">Loading…</div>;
  if (!dn) return <div className="p-8 text-center text-red-500">Delivery note not found.</div>;

  const isOverdue = dn.status !== 'DELIVERED' && new Date(dn.expectedDeliveryAt) < new Date();
  const canSecOut = ['SUPER_ADMIN','ADMIN','SECURITY_OUT'].includes(user.role);
  const canSecIn = ['SUPER_ADMIN','ADMIN','SECURITY_IN'].includes(user.role);
  const canDeliver = dn.receiverId === user.id || ['SUPER_ADMIN','ADMIN'].includes(user.role);

  return (
    <div className="space-y-6 max-w-3xl mx-auto">
      <div className="flex items-center justify-between no-print">
        <div className="flex items-center gap-3">
          <Link to="/delivery-notes" className="text-gray-400 hover:text-gray-600"><ChevronLeft className="w-5 h-5" /></Link>
          <div>
            <h1 className="text-xl font-bold font-mono text-gray-900">{dn.number}</h1>
            <p className="text-sm text-gray-500">Delivery Note</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isOverdue ? <span className="badge badge-overdue">Overdue</span> : <StatusBadge status={dn.status} />}
        </div>
      </div>

      {/* Action buttons */}
      <div className="no-print flex flex-wrap gap-2">
        {dn.status === 'CREATED' && canSecOut && (
          <Button size="sm" icon={Truck} onClick={() => transition.mutate({ action: 'SECURITY_OUT_ACK' })}>Security Out Acknowledge</Button>
        )}
        {['SECURITY_OUT_ACKNOWLEDGED','IN_TRANSIT'].includes(dn.status) && canSecIn && (
          <Button size="sm" icon={Package} onClick={() => transition.mutate({ action: 'SECURITY_IN_ACK' })}>Security In Acknowledge</Button>
        )}
        {['SECURITY_IN_ACKNOWLEDGED','IN_TRANSIT','SECURITY_OUT_ACKNOWLEDGED'].includes(dn.status) && canDeliver && (
          <Button size="sm" variant="success" icon={CheckCircle} onClick={() => transition.mutate({ action: 'DELIVER', receiverRemarks })}>Mark as Delivered</Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card title="Details">
          <div className="p-4 space-y-2 text-sm">
            <Row label="Contents" value={dn.contentDescription} />
            <Row label="Quantity" value={dn.quantity || '—'} />
            <Row label="Sender" value={dn.sender?.name} />
            <Row label="Receiver" value={dn.receiver?.name || dn.receiverText || '—'} />
            <Row label="Remarks" value={dn.remarks || '—'} />
          </div>
        </Card>
        <Card title="Timeline">
          <div className="p-4 space-y-2 text-sm">
            <Row label="Created" value={formatIST(dn.createdAt)} />
            <Row label="Expected by" value={<span className={isOverdue ? 'text-orange-600 font-semibold' : ''}>{formatIST(dn.expectedDeliveryAt)}</span>} />
            {dn.securityOutAt && <Row label="Gate Out" value={`${dn.securityOut?.name} — ${formatIST(dn.securityOutAt)}`} />}
            {dn.securityInAt && <Row label="Gate In" value={`${dn.securityIn?.name} — ${formatIST(dn.securityInAt)}`} />}
            {dn.receiverAcknowledgedAt && <Row label="Delivered" value={formatIST(dn.receiverAcknowledgedAt)} />}
          </div>
        </Card>
      </div>

      <Card title={`Carrier`}>
        <div className="p-4 space-y-2 text-sm">
          <Row label="Type" value={dn.carrierType === 'HAND_CARRY' ? 'Hand Carry' : 'Driver'} />
          {dn.driverName && <Row label="Driver" value={dn.driverName} />}
          {dn.vehicleNumber && <Row label="Vehicle" value={dn.vehicleNumber} />}
        </div>
      </Card>

      <Card title={`Attachments (${dn.attachments?.length || 0})`}>
        <div className="p-4">
          <FileUpload
            deliveryNoteId={id}
            existingFiles={dn.attachments || []}
            disabled={dn.status === 'DELIVERED'}
            onUploaded={() => refetch()}
          />
        </div>
      </Card>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex gap-2">
      <span className="text-gray-500 min-w-28 flex-shrink-0">{label}:</span>
      <span className="font-medium">{value}</span>
    </div>
  );
}
